import axios from "axios";
import React, { useState, useEffect, Fragment } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Form } from "react-bootstrap";
import { API_URL } from "../../../config";
import { useSelector, useDispatch } from "react-redux";
import { logoutonexpire } from "../../../store/actions/AuthActions";
import { setCourses } from "../../../store/actions/CourseAction";

const EditStudent = () => {
  const history = useHistory();
  const { studentId } = useParams();
  const dispatch = useDispatch();
  const collegeId = useSelector((state) => state.college_id.college_id);
  const courses = useSelector((state) => state.courses.courses);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [masterCourseId, setMasterCourseId] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [specId, setSpecId] = useState(null);
  const [passoutYearForm, setPassoutYearForm] = useState("");
  const [interestedPlacement, setInterestedPlacement] = useState(1);
  const [interestedHigherStudy, setInterestedHigherStudy] = useState(1);
  const [specializationData, setSpecializationData] = useState([]);
  const [collegeConnectId, setCollegeConnectId] = useState(null);
  const currentYear = new Date().getFullYear();

  // Load courses into redux if not already loaded
  useEffect(() => {
    const storedCourses = localStorage.getItem("courses");
    if (storedCourses) {
      dispatch(setCourses(JSON.parse(storedCourses)));
    }
  }, [dispatch]);

  // Fetch student details on mount
  useEffect(() => {
    const fetchStudent = async () => {
      if (!studentId) return;
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const authToken = userDetails?.data?.auth_token;

      try {
        const response = await axios.get(
          `${API_URL}/api/freshment_portal_apis/students/get_student_list`,
          {
            params: { college_id: collegeId, search_by_id: studentId, limit: 1, offset: 0 },
            headers: { Authorization: authToken },
          }
        );
        console.log("studentID: ", studentId);
        console.log("response: ", response);

        const list = response.data.data;
        if (list && list.length > 0) {
          const student = list[0];
          setFullName(student.full_name);
          setEmail(student.email);
          setPhoneNumber(student.phone_number || "");
          setMasterCourseId(student.course_id);
          setSpecId(student.specialization_id);
          setPassoutYearForm(student.passout_year);
          setInterestedPlacement(student.interested_in_placement ?? 1);
          setInterestedHigherStudy(student.interested_in_higher_education ?? 1);
          setCollegeConnectId(student.college_connect_id);
        } else {
          toast.error("Student not found.");
          history.push("/students-list");
        }
      } catch (error) {
        if (error?.response?.status === 401) dispatch(logoutonexpire());
        toast.error("Failed to load student details.");
        console.error("Error fetching student:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudent();
  }, [studentId, collegeId]);

  // When masterCourseId changes, resolve college_course_id and load specializations
  useEffect(() => {
    if (masterCourseId !== null && courses && courses.length > 0) {
      const coursemaster = courses.find(
        (c) => c.course_master_id == masterCourseId
      );
      if (coursemaster) {
        setCourseId(coursemaster.college_course_id);

        // Fetch specializations for selected course
        const fetchSpecializations = async () => {
          const userDetails = JSON.parse(localStorage.getItem("userDetails"));
          const authToken = userDetails?.data?.auth_token;
          try {
            const response = await axios.get(
              `${API_URL}/api/freshment_portal_apis/GenericAPI/get_course_spealization_list`,
              {
                params: { college_id: collegeId, course_id: coursemaster.college_course_id },
                headers: { Authorization: authToken },
              }
            );
            setSpecializationData(response.data.data || []);
          } catch (err) {
            if (err?.response?.status === 401) dispatch(logoutonexpire());
          }
        };
        fetchSpecializations();
      }
    } else if (masterCourseId === null) {
      setSpecializationData([]);
      setSpecId(null);
    }
  }, [masterCourseId, courses]);

  const handleSubmit = async () => {
    if (!fullName || fullName === "") {
      toast.error("Please enter full name.");
      return;
    }
    if (!email || email === "") {
      toast.error("Please enter email.");
      return;
    }
    if (!courseId) {
      toast.error("Please select a course.");
      return;
    }
    if (!passoutYearForm || passoutYearForm === "") {
      toast.error("Please select passout year.");
      return;
    }

    const params = new URLSearchParams();
    params.append("student_id", studentId);
    params.append("college_connect_id", collegeConnectId);
    params.append("full_name", fullName);
    params.append("email", email);
    params.append("phone_number", phoneNumber);
    params.append("course_id", courseId);
    params.append("passout_year", passoutYearForm);
    if (specId !== null && specId !== "" && specId !== "Select Specialization") {
      params.append("specialization_id", specId);
    }
    params.append("interested_in_placement", interestedPlacement);
    params.append("interested_in_higher_education", interestedHigherStudy);

    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const authToken = userDetails?.data?.auth_token;

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/freshment_portal_apis/students/update_student_details/${studentId}`,
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
            Authorization: authToken,
          },
        }
      );
      console.log("edit", response);
      if (response.data.success) {
        toast.success("Student details updated successfully!");
        history.push("/students-list");
      } else {
        toast.error(response.data.message || "Failed to update student.");
      }
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logoutonexpire());
      toast.error("Failed to update student.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Fragment>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "300px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {/* Page Header */}
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="primary"
          size="sm"
          onClick={() => history.goBack()}
        >
          <i className="fa fa-arrow-left"></i> Back
        </Button>
        <h4 className="fs-22 font-w600 mb-0 flex-grow-1 text-center">
          Edit Student
        </h4>
        <div style={{ width: "60px" }} />
      </div>

      {/* Form Card */}
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Student Information</h5>
          </div>
          <div className="card-body">
            <Form>
              {/* Full Name */}
              <Form.Group controlId="formFullName" className="mb-3">
                <Form.Label>
                  Full Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Form.Group>

              {/* Email */}
              <Form.Group controlId="formEmail" className="mb-3">
                <Form.Label>
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Form.Group>

              {/* Phone Number */}
              <Form.Group controlId="formPhoneNumber" className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </Form.Group>

              {/* Select Course */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Select Course <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={masterCourseId || "Select Course"}
                  onChange={(e) => {
                    if (e.target.value === "Select Course") {
                      setMasterCourseId(null);
                      setSpecId(null);
                      setSpecializationData([]);
                    } else {
                      setMasterCourseId(e.target.value);
                      setSpecId(null);
                      setSpecializationData([]);
                    }
                  }}
                  id="courseSelect"
                >
                  <option value="Select Course">Select Course</option>
                  {courses?.map((c) => (
                    <option key={c.course_master_id} value={c.course_master_id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              {/* Select Specialization */}
              <Form.Group className="mb-3">
                <Form.Label>Select Specialization</Form.Label>
                <Form.Control
                  as="select"
                  value={specId || "Select Specialization"}
                  onChange={(e) => {
                    if (e.target.value === "Select Specialization") {
                      setSpecId(null);
                    } else {
                      setSpecId(e.target.value);
                    }
                  }}
                  id="branchSelect"
                >
                  <option value="Select Specialization">
                    Select Specialization
                  </option>
                  {specializationData &&
                    specializationData.length > 0 &&
                    specializationData.map((specialize) => (
                      <option
                        key={specialize.specilization_master_id}
                        value={specialize.specilization_master_id}
                      >
                        {specialize.name}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>

              {/* Select Passout Year */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Select Passout Year <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={passoutYearForm}
                  onChange={(e) => setPassoutYearForm(e.target.value)}
                  id="passoutYearSelect"
                >
                  <option value="">Select Year</option>
                  <option value={currentYear - 1}>{currentYear - 1}</option>
                  <option value={currentYear}>{currentYear}</option>
                  <option value={currentYear + 1}>{currentYear + 1}</option>
                  <option value={currentYear + 2}>{currentYear + 2}</option>
                </Form.Control>
              </Form.Group>

              {/* Interested In College Placement */}
              <Form.Group controlId="IntrestedPlacement" className="mb-3">
                <Form.Label>Interested In College Placement</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    label="Yes"
                    value="1"
                    name="interestedPlacement"
                    onChange={() => setInterestedPlacement(1)}
                    checked={interestedPlacement == 1}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="No"
                    value="0"
                    name="interestedPlacement"
                    onChange={() => setInterestedPlacement(0)}
                    checked={interestedPlacement == 0}
                  />
                </div>
              </Form.Group>

              {/* Interested In Higher Studies */}
              <Form.Group controlId="intrestedHigherStudies" className="mb-4">
                <Form.Label>Interested In Higher Studies</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    label="Yes"
                    value="1"
                    name="interestedHigherStudy"
                    onChange={() => setInterestedHigherStudy(1)}
                    checked={interestedHigherStudy == 1}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="No"
                    value="0"
                    name="interestedHigherStudy"
                    onChange={() => setInterestedHigherStudy(0)}
                    checked={interestedHigherStudy == 0}
                  />
                </div>
              </Form.Group>

              {/* Action Buttons */}
              <div className="d-flex gap-3">
                <Button
                  variant="danger btn-sm"
                  onClick={() => history.push("/students-list")}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary btn-sm"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    "Save Student"
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditStudent;
